import { useQuery } from '@tanstack/react-query'
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { useConnectionStore } from '@/shared/realtime'
import { useUiStore } from '@/shared/store/ui-store'
// Direct component paths, not the `@/shared/ui` barrel: this layout is eager
// (always mounted), and importing the barrel here would pull the entire kit —
// DataTable, TanStack Table, every overlay — into the initial download.
import { AnimatedIcon } from '@/shared/ui/animated-icon'
import { Dock, type DockItem } from '@/shared/ui/dock'
import { CreditCardIcon } from '@/shared/ui/icons/credit-card'
import { LayoutGridIcon } from '@/shared/ui/icons/layout-grid'
import { LogoutIcon } from '@/shared/ui/icons/logout'
import { MoonIcon } from '@/shared/ui/icons/moon'
import { RadioTowerIcon } from '@/shared/ui/icons/radio-tower'
import { ReceiptIcon } from '@/shared/ui/icons/receipt'
import { ShieldCheckIcon } from '@/shared/ui/icons/shield-check'
import { SunIcon } from '@/shared/ui/icons/sun'
import { UserIcon } from '@/shared/ui/icons/user'
import { UsersIcon } from '@/shared/ui/icons/users'
import { MobileDock } from '@/shared/ui/mobile-dock'
import { StatusBadge } from '@/shared/ui/status-badge'
import { TopbarPill } from '@/shared/ui/topbar-pill'

import { PERMISSION_ADMIN, signOut } from '@/features/auth'
import type { Session } from '@/features/auth'
import { NotificationsBell } from '@/features/notifications'

// Imported by path, not through `@/app/router`: that barrel also exports the
// router, which imports this layout.
import { can, sessionQuery } from '@/app/router/session'

import { LocaleMenu } from './locale-menu'
import { RouteProgress } from './route-progress'

/**
 * Page chrome. Navigation entries are declared here, in the composition root.
 *
 * The anatomy is the design system's and does not vary: dock rail → content
 * column → floating chrome. There is no sidebar; below 640 the rail becomes a
 * labelled bottom bar.
 *
 * Entries are derived from the session: a tile the guard would bounce the user
 * off is a tile that should never have been offered. Hiding it is an
 * affordance, not enforcement — `beforeLoad` is what actually refuses.
 */
const navFor = (session: Session | null, t: (key: string) => string): NavItem[] =>
  // A forced password change owns the whole session: the guard bounces every
  // other route back, so offering tiles would only produce clicks that undo
  // themselves. Sign-out is the one way out of that state, so it stays — alone,
  // and without the hairline it has nothing to be separated from.
  session?.mustChangePassword === true
    ? [{ ...signOutItem(t), hasDividerBefore: false }]
    : navItemsFor(session, t)

/**
 * A dock tile. Most carry a `to` and navigate; sign-out has none and stays a
 * button, which is what `DockItem` already models.
 */
type NavItem = DockItem

export const SIGN_OUT_ID = 'signOut'

/** No `to`: leaving is an action, not a page. */
const signOutItem = (t: (key: string) => string): NavItem => ({
  id: SIGN_OUT_ID,
  label: t('actions.signOut'),
  icon: <AnimatedIcon icon={LogoutIcon} />,
})

const navItemsFor = (session: Session | null, t: (key: string) => string): NavItem[] => [
  { id: '/', to: '/', label: t('nav.campaigns'), icon: <AnimatedIcon icon={LayoutGridIcon} /> },
  // The everyday half belongs to whoever owns campaigns. An admin has no
  // subscription to pay for, so the tile led them to a page that was always
  // empty. Traffic is read on the campaign that produced it, and has no tile.
  ...(can(session, PERMISSION_ADMIN)
    ? [
        {
          id: '/admin/users',
          to: '/admin/users',
          label: t('nav.users'),
          icon: <AnimatedIcon icon={UsersIcon} />,
        },
        // Invoices, plans and the payment knobs are one subject and now one
        // screen — three views behind a single tile.
        {
          id: '/admin/billing',
          to: '/admin/billing',
          label: t('nav.adminBilling'),
          icon: <AnimatedIcon icon={ReceiptIcon} />,
        },
        {
          id: '/admin/broadcasts',
          to: '/admin/broadcasts',
          label: t('nav.broadcasts'),
          // A tower, not a megaphone: this sends one message to every user at
          // once. A megaphone is one voice pointed at a crowd in the room.
          icon: <AnimatedIcon icon={RadioTowerIcon} />,
        },
        // Правила отсева трафика. Общие для всех кампаний, поэтому свой тайл, а
        // не вид внутри биллинга — те три вида про деньги.
        {
          id: '/admin/global-config',
          to: '/admin/global-config',
          label: t('nav.globalConfig'),
          icon: <AnimatedIcon icon={ShieldCheckIcon} />,
        },
      ]
    : [
        {
          id: '/billing',
          to: '/billing',
          label: t('nav.billing'),
          icon: <AnimatedIcon icon={CreditCardIcon} />,
        },
      ]),
  // Below the hairline: the session itself — who you are and how you leave.
  // Neither is a section of the app, and the pill keeps only what acts in
  // place — the theme switch, the locale menu, notifications.
  {
    id: '/account',
    to: '/account',
    label: t('nav.account'),
    icon: <AnimatedIcon icon={UserIcon} />,
    hasDividerBefore: true,
  },
  signOutItem(t),
]

export function AppLayout() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()

  const { data: session } = useQuery(sessionQuery())
  const status = useConnectionStore((state) => state.status)

  const NAV = navFor(session ?? null, t)

  // `to` travels with the tile now. It used to be stripped here and resolved
  // again in `handleSelect`, which made every dock tile a button — no middle
  // click, no copyable address, and announced as something to press rather than
  // somewhere to go.
  const DOCK_ITEMS: DockItem[] = NAV.map((item) => ({
    id: item.id,
    label: item.label,
    icon: item.icon,
    to: item.to,
    hasDividerBefore: item.hasDividerBefore,
  }))

  // What the bottom bar holds, in order of how often it is opened. The rest
  // folds into a sheet behind one more tile.
  //
  // They used to be dropped outright and «reachable by direct URL», which on a
  // phone is not reachable at all: an admin saw four of their seven sections and
  // had no way to billing, broadcasts or the traffic rules. Losing the tile
  // labels bought room for one more, so only two admin sections fold now — but
  // the bar still cannot hold everything: six tiles at the 44px touch floor is
  // 264px, and a 320px screen leaves 268.
  const MOBILE_PRIMARY = [
    '/',
    '/billing',
    '/admin/users',
    '/admin/billing',
    '/account',
    SIGN_OUT_ID,
  ]
  const MOBILE_DOCK_ITEMS: DockItem[] = DOCK_ITEMS.filter((item) =>
    MOBILE_PRIMARY.includes(item.id),
  )
  const MOBILE_OVERFLOW_ITEMS: DockItem[] = DOCK_ITEMS.filter(
    (item) => !MOBILE_PRIMARY.includes(item.id),
  )

  const theme = useUiStore((state) => state.theme)
  const setTheme = useUiStore((state) => state.setTheme)

  const pathname = useRouterState({ select: (state) => state.location.pathname })
  // `/` matches only exactly: it is the campaigns list, not a prefix of every
  // route. On a page with no tile of its own (`/notifications`) nothing lights
  // up, which is truer than pointing at a page the user is not on.
  const activeId =
    NAV.find((item) => item.to !== undefined && item.to !== '/' && pathname.startsWith(item.to))
      ?.id ??
    (pathname === '/' || pathname.startsWith('/campaigns') ? '/' : undefined)

  const handleSignOut = () => {
    // Sign-out ends the provider session, then clears the query cache, every
    // store, the stream connection and the in-memory token. Navigation is UI
    // work and stays at the call site — `finally`, so a provider that refuses
    // to answer still leaves the user on the login screen.
    void signOut().finally(() => void navigate({ to: '/login', replace: true }))
  }

  // Only the tiles without a `to` reach this — sign-out is the one of them.
  const handleDockSelect = (id: string) => {
    if (id === SIGN_OUT_ID) handleSignOut()
  }

  return (
    <div className="flex h-full">
      {/* Above the floating chrome, which sits at z-40. */}
      <RouteProgress />

      <Dock
        className="max-sm:hidden"
        items={DOCK_ITEMS}
        activeId={activeId}
        onSelect={handleDockSelect}
      />

      {/* `scrollbar-gutter: stable` reserves the 10px the scrollbar takes
          (`::-webkit-scrollbar` in `src/index.css` makes it a classic one, not
          an overlay) whether or not the page is currently long enough to need
          it. Without it every content change that crosses the viewport height
          adds or removes a scrollbar, the column narrows by 10, and everything
          centred in it slides 5px sideways — a left-right twitch on exactly the
          switches where content is replaced. */}
      <main className="min-w-0 flex-1 overflow-auto [scrollbar-gutter:stable]">
        {/* Top padding clears the floating chrome. It sits at `top-[15px]` and
            is 56 tall — a 40px tile inside `py-2` — so its bottom edge is at 71
            and anything above that is under it. `pt-18` was exactly 72: the
            clearance was one pixel, and a page header puts its actions in
            precisely that corner. 88 leaves the gap the eye expects. */}
        <div className="mx-auto flex max-w-column-max flex-col px-page-inline pt-22 pb-8 max-sm:pb-28">
          <Outlet />
        </div>
      </main>

      {/* Floating chrome: never in the content flow, always over it. Inset
          matches the dock rail so both sit the same distance from the edge.
          A `header`, not a `div`: it sits outside `main` and outside the dock's
          `nav`, so as a bare box it was content belonging to no landmark at
          all — which a screen reader's landmark list simply loses. */}
      <header
        aria-label={t('nav.actions')}
        className="fixed top-[15px] right-[15px] z-40 flex items-center gap-2"
      >
        {status === 'reconnecting' && (
          <StatusBadge tone="warning" isLive>
            {t('states.reconnecting')}
          </StatusBadge>
        )}
        <TopbarPill
          label={t('nav.actions')}
          leading={<NotificationsBell />}
          items={[
            { id: 'locale', node: <LocaleMenu />, label: t('actions.language') },
            {
              id: 'theme',
              icon:
                theme === 'dark' ? (
                  <AnimatedIcon icon={SunIcon} />
                ) : (
                  <AnimatedIcon icon={MoonIcon} />
                ),
              label: theme === 'dark' ? t('actions.themeLight') : t('actions.themeDark'),
            },
          ]}
          onSelect={(id) => {
            if (id === 'theme') setTheme(theme === 'dark' ? 'light' : 'dark')
          }}
        />
      </header>

      <MobileDock
        className="fixed inset-x-4 bottom-4 z-40 sm:hidden"
        items={MOBILE_DOCK_ITEMS}
        overflowItems={MOBILE_OVERFLOW_ITEMS}
        activeId={activeId}
        onSelect={handleDockSelect}
      />
    </div>
  )
}
